<template>
	<app-button icon="plus" size="small" @click="open = true">Add Document</app-button>
	<slideover title="Add Document" :open="open" @close="close">
		<app-select
			name="type"
			label="Type"
			:model-value="form.type"
			:options="documentTypes"
			label-key="name"
			value-key="id"
			:disabled="submitting"
			:error="errors['type']"
			@update:model-value="updateType">
		</app-select>

		<template v-if="form.type">
			<app-alert
				v-if="selectedDocumentType?.requires_review"
				theme="warning"
				class="mt-3 mb-1">
				<div class="flex items-start gap-2">
					<div>
						<p class="font-semibold text-sm">Review required before publication</p>
						<p class="text-xs mt-0.5">
							This document type must be approved by a reviewer before it becomes
							visible to general users.
						</p>
					</div>
				</div>
			</app-alert>

			<div class="my-5">
				<app-file-button name="Add" :accept="'.' + allowableExt" @file-change="upload">
					{{ form.file ? 'Change File' : 'Select File' }}
				</app-file-button>
			</div>
		</template>

		<template v-if="form.file">
			<app-input
				label="Reference"
				:model-value="form.reference"
				:disabled="submitting"
				:error="errors['reference']"
				@update:model-value="(value) => setFieldValue(`reference`, value)">
			</app-input>

			<app-input
				label="Name"
				:model-value="form.name"
				:disabled="submitting"
				:error="errors['name']"
				@update:model-value="(value) => setFieldValue(`name`, value)">
			</app-input>

			<div class="flex">
				<app-input
					class="w-1/3"
					label="Revision No"
					:model-value="form.revision_no"
					:disabled="submitting"
					:error="errors['revision_no']"
					@update:model-value="(value) => setFieldValue(`revision_no`, value)">
				</app-input>

				<app-input
					class="w-2/3 pl-5"
					label="Revision Description"
					:model-value="form.revision_description"
					:disabled="submitting"
					:error="errors['revision_description']"
					@update:model-value="(value) => setFieldValue(`revision_description`, value)">
				</app-input>
			</div>

			<app-date-input
				label="Date"
				:model-value="form.date"
				:disabled="submitting"
				:error="errors['date']"
				@update:model-value="(value) => setFieldValue(`date`, value)">
			</app-date-input>

			<app-select
				name="language_id"
				label="Language"
				:model-value="form.language_id"
				:options="languages"
				label-key="name"
				value-key="id"
				:disabled="submitting"
				:error="errors['language_id']"
				@update:model-value="(value) => setFieldValue('language_id', value)">
			</app-select>

			<app-select
				v-if="selectedDocumentType?.category?.access_control_type !== 'brand'"
				name="business_unit_id"
				label="Business Unit"
				:model-value="form.business_unit_id"
				:options="businessUnits"
				label-key="name"
				value-key="id"
				:disabled="submitting"
				:error="errors['business_unit_id']"
				@update:model-value="(value) => setFieldValue('business_unit_id', value)">
			</app-select>

			<app-select
				v-if="selectedDocumentType?.category?.access_control_type === 'brand'"
				name="brand"
				label="Brand"
				:model-value="form.brand"
				:options="brands"
				label-key="name"
				value-key="id"
				:disabled="submitting"
				:error="errors['brand']"
				@update:model-value="(value) => setFieldValue('brand', value)">
			</app-select>
		</template>

		<div class="w-full">
			<app-alert v-if="errorMessage" theme="danger" class="text-left mt-3">
				{{ errorMessage }}
			</app-alert>
		</div>

		<template #action>
			<div class="w-full">
				<div class="flex justify-end">
					<app-button @click="submit">Save</app-button>
				</div>
			</div>
		</template>
	</slideover>
</template>

<script lang="ts" setup>
	import { onMounted, ref } from 'vue'
	import useToaster from '@/stores/toaster'
	import { useForm } from 'vee-validate'
	import * as Yup from 'yup'
	import AppFileButton from '@/components/base/forms/AppFileButton.vue'
	import FileService from '@/services/file'
	import { File } from '@/data/schema/file'
	import DocumentTypeService from '@/services/document-type'
	import AppDateInput from '@/components/base/forms/AppDateInput.vue'
	import { YMD } from '@/utils/date'
	import DocumentService from '@/services/document'
	import BusinessUnitsService from '@/services/business-units'
	import BrandsService from '@/services/brands'
	import LanguagesService from '@/services/languages'
	import AppAlert from '@/components/base/AppAlert.vue'

	const toaster = useToaster()

	const documentTypeApi = new DocumentTypeService()
	const businessUnitsApi = new BusinessUnitsService()
	const brandsApi = new BrandsService()
	const languagesApi = new LanguagesService()

	const documentTypes = ref<any>([])
	const languages = ref<any>([])
	const businessUnits = ref<any>([])
	const brands = ref<any>([])
	const errorMessage = ref<string | null>(null)
	const selectedDocumentType = ref<any>(null)

	const fileApi = new FileService()
	const submitting = ref<boolean>(false)

	const allowableExt = ref<string>('pdf')

	const documentApi = new DocumentService()

	const props = withDefaults(
		defineProps<{
			product?: string
			productCategory?: string
		}>(),
		{
			product: null,
			productCategory: null
		}
	)

	const open = ref(false)

	const {
		handleSubmit,
		resetForm,
		values: form,
		errors,
		setFieldValue
	} = useForm<{
		type: string
		reference: string
		name: string
		revision_description: string
		revision_no: string
		date: string
		status: string
		file: File
		product: string
		product_category: string
		language_id: string | number
		business_unit_id: string
		brand: string
	}>({
		initialValues: {
			type: null,
			reference: null,
			name: null,
			revision_description: null,
			revision_no: null,
			status: 'S2',
			date: YMD(new Date()),
			file: null,
			product: props.product,
			product_category: props.productCategory,
			language_id: 28,
			business_unit_id: 'MHL',
			brand: null
		},
		validationSchema: Yup.object({
			name: Yup.string().nullable().min(5, 'The Name must be at least 5 characters.'),
			reference: Yup.string()
				.nullable()
				.min(5, 'The Reference must be at least 5 characters.'),
			revision_description: Yup.string()
				.nullable()
				.min(5, 'The Revision Description must be at least 5 characters.'),
			revision_no: Yup.string()
				.nullable()
				.max(3, 'The Revision No must not be greater than 3 characters.'),
			language_id: Yup.string().required('The Language is required.'),
			business_unit_id: Yup.lazy(() => {
				if (selectedDocumentType.value?.category?.access_control_type === 'brand') {
					return Yup.string().nullable()
				} else {
					return Yup.string().required('The Business Unit is required.')
				}
			}),
			brand: Yup.lazy(() => {
				if (selectedDocumentType.value?.category?.access_control_type === 'brand') {
					return Yup.string().required('The Brand is required.')
				} else {
					return Yup.string().nullable()
				}
			})
		})
	})

	function close() {
		open.value = false
		resetForm()
		errorMessage.value = null
	}

	// Events
	const emit = defineEmits(['completed'])

	const submit = handleSubmit(
		(values) => {
			submitting.value = true
			errorMessage.value = null

			documentApi
				.create(values)
				.then(() => {
					toaster.success({ text: 'Document added' })
					close()
					emit('completed', true)
				})
				.catch((error) => {
					if (error.response && error.response.status === 422) {
						errorMessage.value = error.response.data.message
					} else {
						toaster.error({ text: 'An unexpected error occurred.' })
					}
				})
				.finally(() => {
					submitting.value = false
				})
		},
		(errors) => {
			console.error(errors)
		}
	)

	const getDocumentTypes = async () => {
		const res = await documentTypeApi.getAll()
		documentTypes.value = res.data
	}

	const getBusinessUnits = async () => {
		const res = await businessUnitsApi.getAll()
		businessUnits.value = res.data
	}

	const getBrands = async () => {
		const res = await brandsApi.getAll()
		brands.value = res.data
	}

	const getLanguages = async () => {
		const res = await languagesApi.getAll()
		languages.value = res.data
	}

	const updateType = (value) => {
		setFieldValue('type', value)
		setFieldValue('reference', null)
		setFieldValue('name', null)
		setFieldValue('revision_description', null)
		setFieldValue('revision_no', null)
		setFieldValue('date', YMD(new Date()))
		setFieldValue('file', null)
		setFieldValue('business_unit_id', null)
		setFieldValue('brand', null)
		
		selectedDocumentType.value = documentTypes.value.find((type) => type.id == value)
		allowableExt.value = selectedDocumentType.value?.allowable_ext
		errorMessage.value = null // Clear error when type changes
	}

	const upload = (value: any[]) => {
		const file = value[0]
		
		// Clear any existing error when file selection starts
		errorMessage.value = null
		
		// Validate file extension
		const fileExtension = file.name.split('.').pop()?.toLowerCase()
		if (fileExtension !== allowableExt.value.toLowerCase()) {
			errorMessage.value = `Invalid file type. Only .${allowableExt.value} files are allowed.`
			return
		}
		
		const parts = file.name.split(' - ')

		const match = file.name.match(/Issue\s+(\d+)/)
		const issueNumber = match ? match[1] : null

		setFieldValue('reference', parts.length > 1 ? parts[0] : null)
		setFieldValue('name', parts.length > 1 ? parts[1] : null)
		setFieldValue('revision_no', issueNumber)
		fileApi.upload(value[0]).then((res) => {
			setFieldValue('file', res.data.file)
		})
	}

	onMounted(() => {
		getDocumentTypes()
		getBusinessUnits()
		getBrands()
		getLanguages()
	})
</script>
